import { DateTime } from "luxon";
import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import SubTaskList from "../reusable-elements/sub-task-list";
import { SmartInput } from "../reusable-elements/floating-smart-input";
import DateTimePicker from "../calendar/datetime-picker";
import ExtendedDate from "../../app/extended-date";
import { KanbanContext } from './../../pages/kanban-board/kanban-board';
import { MaintenanceKanbanContext } from '../properties/maintenance-tab';
import { InspectionKanbanContext } from '../properties/inspection-kanban';
import { notNullOrEmpty, validateField } from "../../utils/validators";
import Comparer from "../../app/comparer";
import useTrackedProperty from './../../app/useTrackedProperty';

const InnerTile = ({ project, tile, editable, deletable, showPriorityBadge, showViewDetailsButton, onViewDetails }) => {

    const humanizeDate = (d) => {
        return DateTime.fromISO(d).toRelativeCalendar();
    }

    const getDefaultDate = () => {
        let defaultDate = new ExtendedDate(new Date());
        defaultDate = defaultDate
            .setHoursAndReturnNewDate(12)
            .setMinutesAndReturnNewDate(0)
            .setSecondsAndReturnNewDate(0)
            .setMillisecondsAndReturnNewDate(0);
        return defaultDate.getDate();
    }

    const comparer = useMemo(() => new Comparer(), []);

    // Try to get context from KanbanContext, MaintenanceKanbanContext, or InspectionKanbanContext
    const kanbanContext = useContext(KanbanContext);
    const maintenanceContext = useContext(MaintenanceKanbanContext);
    const inspectionContext = useContext(InspectionKanbanContext);
    const context = kanbanContext || maintenanceContext || inspectionContext;
    const { setData, upsertTile, markTileToDelete } = context || {};

    const [state, setState] = useState(() => structuredClone(tile));
    const [originalState, setOriginalState] = useState(() => structuredClone(tile));

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerDate, setDatePickerDate] = useState(tile.dueDate ? new Date(tile.dueDate) : getDefaultDate());
    const [validationErrors, setValidationErrors] = useState(null);

    const tileSaving = useTrackedProperty(Object.hasOwn(tile, "saving") ? tile : state, "saving");

    const hasChanges = useCallback(() => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    }, [state, originalState, comparer]);

    useEffect(() => {
        setState(structuredClone(tile));
        setOriginalState(structuredClone(tile));
    }, [tile.id]);

    useEffect(() => {
        if (tileSaving !== undefined) {
            setState(current => structuredClone({ ...current, saving: tileSaving }));
            setOriginalState(current => structuredClone({ ...current, saving: tileSaving }));
        }
    }, [tileSaving]);

    const localToggleSubTaskComplete = subTaskId => {
        if (setData) {
            setData(boardsOrBoard => {
                if (Array.isArray(boardsOrBoard)) {
                    // Main kanban context - array of boards
                    const board = boardsOrBoard.find(b => b.id === project.id);
                    const column = board.columns.find(c => c.columnId === state.columnId);
                    const currentTile = column.items.find(t => t.id === state.id);
                    const subTask = currentTile.subTasks.find(st => st.subTaskId === subTaskId);
                    subTask.isCompleted = !subTask.isCompleted;
                    return boardsOrBoard;
                } else {
                    // Maintenance context - single board
                    const column = boardsOrBoard.columns.find(c => c.columnId === state.columnId);
                    const currentTile = column.items.find(t => t.id === state.id);
                    const subTask = currentTile.subTasks.find(st => st.subTaskId === subTaskId);
                    subTask.isCompleted = !subTask.isCompleted;
                    return boardsOrBoard;
                }
            });
        }
    };

    const handleSaveClicked = async () => {
        setShowDatePicker(!showDatePicker);
        if (showDatePicker) {
            console.log(`Save date: ${datePickerDate}`)
            state.dueDate = datePickerDate.toISOString();
            await upsertTile(state);
        }
    }

    const handleDateChange = date => {
        setDatePickerDate(date);
    }

    const cancelEditDueDate = () => {
        setShowDatePicker(false);
        setDatePickerDate(getDefaultDate());
    }

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.name), () => stateToValidate.name, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {
        const newState = updateMethod(state);
        validateState(newState);
        setState(newState);
    };



    const canSave = () => {

        if (tileSaving) { return false }

        let result = hasChanges();
        result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
        return result;
    };

    const handleSaveButtonClicked = async () => {
        setState(current => structuredClone({ ...current, saving: true }));
        if (!tileSaving) {
            await upsertTile(state);
            setState(current => structuredClone({ ...current, saving: false }));
            setOriginalState(structuredClone(state));
        }
    };

    const handleSubTaskChanges = newTile => {
        if (setData) {
            setData(boardsOrBoard => {
                if (Array.isArray(boardsOrBoard)) {
                    // Main kanban context - array of boards
                    const board = boardsOrBoard.find(b => b.id === project.id);
                    const column = board.columns.find(c => c.columnId === state.columnId);
                    const currentTileIndex = column.items.findIndex(t => t.id === newTile.id);
                    if (currentTileIndex > -1) {
                        column.items.splice(currentTileIndex, 1, newTile);
                    }
                    return boardsOrBoard;
                } else {
                    // Maintenance context - single board
                    const column = boardsOrBoard.columns.find(c => c.columnId === state.columnId);
                    const currentTileIndex = column.items.findIndex(t => t.id === newTile.id);
                    if (currentTileIndex > -1) {
                        column.items.splice(currentTileIndex, 1, newTile);
                    }
                    return boardsOrBoard;
                }
            });
        }

        setState(structuredClone(newTile));
        validateState(newTile);
    };

    return (
        <div disabled={tileSaving} className={`list-group-item d-flex border-top-0 list-group-item-action ${tileSaving ? 'disabled' : ''}`}>

            {deletable &&
                <div key={`project-${project.projectId}-tile-${state.tileId}`} className="form-check mb-1">
                    <input id="deleteTask" className="form-check-input is-invalid" type='checkbox' onChange={() => markTileToDelete(state)} />
                </div>
            }

            <div className="flex-fill">
                <SmartInput
                    label="Title"
                    className="fs-12px lh-12 mb-2px fw-bold text-dark w-100"
                    text={state.name}
                    updateCallback={newValue => { handleStateUpdates(s => { s.name = newValue; return s; }) }}
                    readOnly={false}
                    required={true}
                    validatorFn={text => notNullOrEmpty(text)}
                />
                <SmartInput
                    label="Description"
                    className="fs-12px lh-12 mb-2px fw-bold text-dark w-100"
                    text={state.description}
                    updateCallback={newValue => { handleStateUpdates(s => { s.description = newValue; return s; }) }}
                    required={false}
                    readOnly={false}
                    validatorFn={text => notNullOrEmpty(text)}
                />

                <div className="mt-5px">
                    <div className="text-gray-600 flex-1">Opened: {(humanizeDate(state.created) ?? "Unknown") + " "}
                        <i style={{ cursor: "pointer" }} title={state.created ? DateTime.fromISO(state.created).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : "Unknown"} className="far fa-question-circle fa-lg text-gray-500 fa-fw"></i> <> {state && state.createdBy && state.createdBy.name && (<>by {state.createdBy.name} </>)} </>
                    </div>
                    <div className="text-gray-600 flex-1">Due date: {state.dueDate ? humanizeDate(state.dueDate) : "Not Set"}
                        <i style={{ cursor: "pointer" }} title={state.dueDate ? DateTime.fromISO(state.dueDate).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : "Please set a date"} className="far fa-question-circle text-gray-500 fa-fw fa-lg"></i>

                        {editable ?
                            <>
                                <i title={showDatePicker ? "Save Date" : "Set a date"}
                                    style={{ cursor: "pointer" }}
                                    className={showDatePicker ? "far fa-save fa-3x text-white-500 fa-fw" : "far fa-calendar fa-lg text-gray-500 fa-fw"}
                                    onClick={handleSaveClicked} >
                                </i>
                                <i title={"Cancel"}
                                    style={{ cursor: "pointer" }}
                                    className={showDatePicker ? "far fa-circle-xmark fa-3x text-white-500 fa-fw" : "invisible"}
                                    onClick={cancelEditDueDate} >
                                </i>
                            </>
                            :
                            <></>
                        }

                        <div className="text-gray-600 flex-1">Completed: {(humanizeDate(state.completed) ?? "Not Complete") + " "}
                            <i style={{ cursor: "pointer" }} title={state.completed ? DateTime.fromISO(state.completed).toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : "Unknown"} className="far fa-question-circle fa-lg text-gray-500 fa-fw"></i> <> {state && state.completedBy && state.completedBy.name && (<>by {state.completedBy.name} </>)} </>
                        </div>


                        {showDatePicker &&
                            <DateTimePicker
                                fetchComplete={true}
                                openDtp={showDatePicker}
                                showTime={true}
                                dateValue={datePickerDate}
                                placeholder="Select 'Due By' Date"
                                stopEditDate={cancelEditDueDate}
                                setDate={handleDateChange}
                                editDate={() => setShowDatePicker(true)}
                            />
                        }

                    </div>
                </div>

                <div className="mb-1 d-flex justify-content-between align-items-start">
                    <div>
                        {state && state.tags && Array.isArray(state.tags) && state.tags.map((tag, i) => (
                            <span key={`tag-${state.id}-${i}`} style={{ marginRight: '5px' }} className="badge bg-blue">{tag}</span>
                        ))}
                        {((state.photos && state.photos.length > 0) || (state.subTasks && state.subTasks.some(st => st.photos && st.photos.length > 0))) && (
                            <div className="text-gray-600 mt-1">
                                <i className="fas fa-paperclip fa-lg text-danger" title="Has attachments"></i> Attachments
                            </div>
                        )}
                    </div>
                    {showPriorityBadge && (
                        <div style={{ pointerEvents: 'none' }}>
                            {state.priority && (
                                <span className={`badge ${
                                    state.priority === 'Critical' ? 'bg-danger' :
                                    state.priority === 'High' ? 'bg-warning' :
                                    state.priority === 'Medium' ? 'bg-info' :
                                    state.priority === 'Low' ? 'bg-light text-dark' : 'bg-light text-dark'
                                }`}>
                                    {state.priority}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <SubTaskList
                    project={project}
                    tile={state}
                    parent="tile-component"
                    toggleSubTaskComplete={localToggleSubTaskComplete}
                    allowEdits={editable}
                    updateTile={handleSubTaskChanges}
                />
                <div className="btn-group mt-10px">
                    {showViewDetailsButton && (
                        <button 
                            type="button" 
                            className="btn btn-outline-primary btn-sm me-2" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewDetails?.(state);
                            }}
                        >
                            <i className="fa fa-eye"></i>&nbsp;&nbsp;View Details
                        </button>
                    )}
                    {(canSave() || !!tileSaving) && <button type="button" className="btn btn-primary" onClick={handleSaveButtonClicked}>
                        <i className={tileSaving ? `fas fa-spinner fa-spin` : `fa fa-save`}></i>&nbsp;&nbsp;{`${tileSaving ? 'saving please wait...' : 'Save'}`}
                    </button>}
                </div>
            </div>
        </div>
    );
};

export default InnerTile;